import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectChatSession } from 'flow/selectors/chat-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { State } from 'flow/types/State';
import { LatestMessage } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { extractAbbreviatedName, getFormattedTime } from 'flow/utils';
import { messageIsFromExtension } from 'flow/utils/message-utils';
import { twMerge } from 'tailwind-merge';
import SessionsListContextMenu from 'flow/components/context-menus/SessionsListContextMenu';
import ContextMenu from 'flow/components/ContextMenu';
import BaseListItem, {
    rowMarking,
} from 'flow/components/ui-components/BaseListItem';
import ChatAvatar from 'flow/components/ui-components/ChatAvatar';
import Icon from 'flow/components/ui-components/Icon';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    chatSessionKey: ChatSessionKey;
    /** Whether this chat's page is open. */
    isOpen: boolean;
}

const ChatSessionListItem = ({ chatSessionKey, isOpen }: Props) => {
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const history = useHistory();

    /* Its own session, by key. Taking the whole sessions map as a prop meant
     * every row's props changed whenever a message arrived in any chat. */
    const session = useAppSelector((state: State) =>
        selectChatSession(state, chatSessionKey),
    );
    const latestMessage = session.get('latestMessage');

    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const getSenderName = (message: TypedMap<LatestMessage>) => {
        if (!message) {
            //No message means no sender, so it has to be empty
            return (
                <span className="text-gray-400">
                    {i18next.t('chatlist.nomessages')}
                </span>
            );
        }
        if (!message?.has('from')) {
            return i18next.t('status.unknown') + ':';
        }
        const from = message.get('from');

        if (messageIsFromExtension(message, ownExtensionKey)) {
            return (
                <span className="text-chatYou">
                    {i18next.t('chat.you') + ':'}
                </span>
            );
        }
        return (
            extractAbbreviatedName(
                from.get('contact'),
                i18next.t('status.unknown'),
            ) + ':'
        );
    };

    return (
        <BaseListItem
            marking={rowMarking({
                pageOpen: isOpen,
                highlighted: contextMenuIsActive,
            })}
            className="badge-bg"
            onClick={() =>
                history.push(
                    generatePath(routes['messages.id'], { id: chatSessionKey }),
                )
            }
            data-test-id={`chat-chatsession-${chatSessionKey}`}
        >
            <ChatAvatar session={session} />

            <div className="flex-1 truncate">
                <div className="flex justify-between">
                    <div className="truncate">
                        <p
                            className={twMerge(
                                'truncate text-base/5',
                                session.get('numberOfUnreadMessages') > 0
                                    ? 'font-bold'
                                    : '',
                            )}
                        >
                            {session.get('name')}
                        </p>

                        <div className="flex flex-row items-center text-sm">
                            {getSenderName(latestMessage)}&nbsp;
                            {latestMessage?.get('message') ? (
                                <p
                                    dir="auto"
                                    className="truncate text-sm text-gray-500"
                                >
                                    {latestMessage?.get('message')}
                                </p>
                            ) : (
                                <>
                                    {latestMessage?.get('attachment') && (
                                        <div className="flex flex-row items-center truncate text-sm text-gray-500">
                                            <Icon
                                                iconKey="attachment"
                                                className="text-gray-500"
                                                size="1rem"
                                            />
                                            <div className="truncate">
                                                {latestMessage.getIn([
                                                    'attachment',
                                                    'fileName',
                                                ])}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex h-full justify-end self-start">
                <div
                    className={twMerge(
                        'flex h-full flex-col',
                        contextMenuIsActive
                            ? 'hidden'
                            : 'group-focus-visible-within:hidden group-hover:hidden',
                    )}
                >
                    <div className="text-end text-xs/5 text-nowrap text-gray-500">
                        {getFormattedTime(latestMessage?.get('sent'), i18next)}
                    </div>

                    <div className="flex justify-end gap-x-1">
                        {session.get('chatMuted') && (
                            <div className="mt-auto flex size-5 items-center justify-center self-end rounded-sm bg-gray-200">
                                <Icon
                                    size="16"
                                    iconKey="notificationsOff"
                                    className="text-gray-500"
                                />
                            </div>
                        )}

                        {session.get('chatArchived', false) && (
                            <div className="mt-auto flex size-5 items-center justify-center self-end rounded-sm bg-gray-200">
                                <Icon
                                    size="16"
                                    iconKey="archive"
                                    className="text-gray-500"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div
                className={twMerge(
                    contextMenuIsActive
                        ? 'flex'
                        : 'sr-only-until-hover-or-focus flex',
                )}
            >
                <ContextMenu
                    triggerLabel={getMoreOptionsLabel(
                        i18next,
                        session.get('name'),
                    )}
                    setActive={setContextMenuIsActive}
                    lockScroll={{
                        scroll: document.querySelector('.left-nav-scroll'),
                        wrapper: document.querySelector(
                            '.left-nav-scroll-wrapper',
                        ),
                    }}
                >
                    <SessionsListContextMenu session={session} />
                </ContextMenu>
            </div>
        </BaseListItem>
    );
};

/* Memoised on its props. The list passes `isOpen`: a read of the route here
 * would render every row on each change of the address. */
export default React.memo(ChatSessionListItem);
